import type {
  Appearance,
  Comparison,
  DiscoverHistoryResult,
  HistoryScan,
  OptimisedFixture,
  FixturePrediction,
  IngestResult,
  League,
  LineupSlot,
  PlayerResultRow,
  PrefillRow,
  RatedPlayer,
  StatisticsRow,
  StatOverride,
  StatOverrideInput,
  Team,
} from './types';

const BASE = import.meta.env.VITE_API_BASE_URL ?? '/api';

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  // Only declare a JSON body when there actually is one — Fastify rejects a
  // request that announces application/json and then sends nothing, which is
  // exactly what a bodyless DELETE would do.
  const headers: Record<string, string> = { ...((init?.headers as Record<string, string>) ?? {}) };
  if (init?.body !== undefined && headers['content-type'] === undefined) {
    headers['content-type'] = 'application/json';
  }

  const response = await fetch(BASE + path, { ...init, headers });

  // 204 has no body to parse.
  if (response.status === 204) return undefined as T;

  if (!response.ok) {
    // The API answers errors as { error, message }; fall back to the status text
    // when something else (a proxy, say) produced the failure.
    let code = 'http_error';
    let message = `${response.status} ${response.statusText}`;
    try {
      const body = (await response.json()) as { error?: string; message?: string };
      code = body.error ?? code;
      message = body.message ?? message;
    } catch {
      /* keep the status-based message */
    }
    throw new ApiError(response.status, code, message);
  }

  return (await response.json()) as T;
}

export const api = {
  leagues: () => request<League[]>('/leagues'),
  league: (id: number) => request<League>(`/leagues/${id}`),
  teams: (leagueId: number) => request<Team[]>(`/leagues/${leagueId}/teams`),

  players: (leagueId: number, teamId?: number) =>
    request<RatedPlayer[]>(`/leagues/${leagueId}/players${teamId ? `?teamId=${teamId}` : ''}`),

  statistics: (leagueId: number, kind?: 'SINGLE' | 'DOUBLE') =>
    request<StatisticsRow[]>(`/leagues/${leagueId}/statistics${kind ? `?kind=${kind}` : ''}`),

  playerResults: (leagueId: number) => request<PlayerResultRow[]>(`/leagues/${leagueId}/player-results`),

  suggestedLineup: (leagueId: number, teamId: number) =>
    request<LineupSlot[]>(`/leagues/${leagueId}/suggested-lineup?teamId=${teamId}`),

  ingest: (url: string, deep: boolean) =>
    request<IngestResult>('/ingest', { method: 'POST', body: JSON.stringify({ url, deep }) }),

  compare: (leagueId: number, left: number[], right: number[]) =>
    request<Comparison>('/compare', { method: 'POST', body: JSON.stringify({ leagueId, left, right }) }),

  predict: (leagueId: number, home: LineupSlot[], away: LineupSlot[]) =>
    request<FixturePrediction>('/predict/fixture', {
      method: 'POST',
      body: JSON.stringify({ leagueId, home, away }),
    }),

  manualStats: (leagueId: number) => request<StatOverride[]>(`/leagues/${leagueId}/manual-stats`),

  saveManualStats: (leagueId: number, playerId: number, body: StatOverrideInput) =>
    request<StatOverride>(`/leagues/${leagueId}/players/${playerId}/manual-stats`, {
      method: 'PUT',
      body: JSON.stringify(body),
    }),

  clearManualStats: (leagueId: number, playerId: number) =>
    request<void>(`/leagues/${leagueId}/players/${playerId}/manual-stats`, { method: 'DELETE' }),

  manualStatsPrefill: (leagueId: number) =>
    request<PrefillRow[]>(`/leagues/${leagueId}/manual-stats/prefill`),

  optimiseLineup: (body: {
    leagueId: number;
    teamId: number;
    opponentTeamId?: number;
    opponentLineup?: LineupSlot[];
    coreSize?: number;
    maxSwitchouts?: number;
  }) => request<OptimisedFixture>('/optimise/lineup', { method: 'POST', body: JSON.stringify(body) }),

  leagueHistory: (leagueId: number) =>
    request<{ lastScan: HistoryScan | null; appearances: Appearance[] }>(`/leagues/${leagueId}/history`),

  discoverHistory: (leagueId: number, body: { includeSameSeason?: boolean; maxLeagues?: number }) =>
    request<DiscoverHistoryResult>(`/leagues/${leagueId}/discover-history`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
};
